import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Room,
  RoomEvent,
  Track,
  type LocalTrack,
  type Participant,
} from 'livekit-client';
import type { LiveKitCredentials } from '../../shared/types';
import { get, post } from '../api';
import { selectCameras, type CameraInfo } from '../callDevices';
import { useChat } from '../state';

export const AI_REQUEST_TOPIC = 'chatsi.ai.request';
export const AI_REPLY_TOPIC = 'chatsi.ai';

export interface CallTile {
  id: string;
  name: string;
  local: boolean;
  screen?: boolean;
  video?: Track;
  speaking?: boolean;
}

export type CallStatus = 'idle' | 'connecting' | 'connected' | 'error';

function collectTiles(room: Room): CallTile[] {
  const tiles: CallTile[] = [];
  const push = (participant: Participant, local: boolean) => {
    const camera = participant.getTrackPublication(Track.Source.Camera);
    const screen = participant.getTrackPublication(Track.Source.ScreenShare);
    const name = participant.name || participant.identity;
    if (screen?.track && !screen.isMuted) {
      tiles.push({ id: `${participant.identity}-screen`, name, local, screen: true, video: screen.track });
    }
    tiles.push({
      id: participant.identity,
      name,
      local,
      video: camera?.track,
      speaking: participant.isSpeaking,
    });
  };
  if (room.localParticipant) push(room.localParticipant, true);
  for (const participant of room.remoteParticipants.values()) push(participant, false);
  return tiles;
}

export function useCall(conversationId: string | null) {
  const roomRef = useRef<Room | null>(null);
  const [status, setStatus] = useState<CallStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [tiles, setTiles] = useState<CallTile[]>([]);
  const [mic, setMic] = useState(true);
  const [camera, setCamera] = useState(false);
  const [cameras, setCameras] = useState<CameraInfo[]>([]);
  const [canSwitchCamera, setCanSwitchCamera] = useState(false);
  const [activeCameraId, setActiveCameraId] = useState<string | null>(null);
  const [switchingCamera, setSwitchingCamera] = useState(false);
  const [screen, setScreen] = useState(false);
  const [agent, setAgent] = useState(false);
  const [agentBusy, setAgentBusy] = useState(false);
  const me = useChat((state) => state.me);
  // Resolves when the agent's reply stream for a messageId actually starts.
  const startWaiters = useRef(new Map<string, () => void>());

  const waitForReplyStart = useCallback((messageId: string, timeoutMs: number) => {
    return new Promise<boolean>((resolve) => {
      let settled = false;
      let timer: ReturnType<typeof setTimeout>;
      const finish = (started: boolean) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        startWaiters.current.delete(messageId);
        resolve(started);
      };
      timer = setTimeout(() => finish(false), timeoutMs);
      startWaiters.current.set(messageId, () => finish(true));
    });
  }, []);

  const refresh = useCallback((room: Room) => {
    setTiles(collectTiles(room));
    setMic(room.localParticipant.isMicrophoneEnabled);
    setCamera(room.localParticipant.isCameraEnabled);
    setScreen(room.localParticipant.isScreenShareEnabled);
  }, []);

  // Labels only become available after camera permission, so this is refreshed
  // on join, on publish and after every switch. A single-camera device simply
  // reports canSwitchCamera=false and the UI disables switching.
  const refreshCameras = useCallback(async () => {
    const room = roomRef.current;
    if (!navigator.mediaDevices?.enumerateDevices) {
      setCameras([]);
      setCanSwitchCamera(false);
      setActiveCameraId(null);
      return;
    }
    try {
      const devices = await Room.getLocalDevices('videoinput', false);
      const selection = selectCameras(devices, room?.getActiveDevice('videoinput'));
      setCameras(selection.cameras);
      setCanSwitchCamera(selection.canSwitch);
      setActiveCameraId(selection.activeId);
    } catch {
      setCameras([]);
      setCanSwitchCamera(false);
      setActiveCameraId(null);
    }
  }, []);

  const leave = useCallback(async (stopAgent = true) => {
    const room = roomRef.current;
    roomRef.current = null;
    if (room) {
      await room.disconnect().catch(() => {});
    }
    if (stopAgent && conversationId && agent) {
      await post('/api/livekit/agent', { conversationId, action: 'stop' }).catch(() => {});
    }
    setStatus('idle');
    setTiles([]);
    setScreen(false);
    setCamera(false);
    setCameras([]);
    setCanSwitchCamera(false);
    setActiveCameraId(null);
    setSwitchingCamera(false);
    setAgent(false);
  }, [conversationId, agent]);

  const join = useCallback(async () => {
    if (!conversationId) return;
    if (roomRef.current) return;
    setStatus('connecting');
    setError(null);
    try {
      const identity = me.id.replace(/[^a-zA-Z0-9_-]/g, '-');
      const credentials = await get<LiveKitCredentials>(
        `/api/livekit/token?conversationId=${encodeURIComponent(conversationId)}` +
          `&identity=${encodeURIComponent(identity)}` +
          `&name=${encodeURIComponent(me.name || '')}`,
      );
      const room = new Room({ adaptiveStream: true, dynacast: true });
      roomRef.current = room;

      room.registerTextStreamHandler(AI_REPLY_TOPIC, (reader) => {
        void (async () => {
          const attributes = reader.info.attributes ?? {};
          const target = attributes.conversationId ?? conversationId;
          const messageId = attributes.messageId ?? `live-${Date.now()}`;
          let text = '';
          useChat.getState().addEphemeral(target, messageId);
          startWaiters.current.get(messageId)?.();
          try {
            for await (const chunk of reader) {
              text += chunk;
              useChat.getState().appendStream(target, messageId, text);
            }
          } catch {
            // stream aborted
          }
          useChat.getState().finishStream(target, messageId);
          if (text.trim()) {
            await post(`/api/conversations/${encodeURIComponent(target)}/ai/append`, {
              id: messageId,
              text: text.trim(),
            }).catch(() => {});
          }
        })();
      });

      const update = () => refresh(room);
      room
        .on(RoomEvent.ParticipantConnected, update)
        .on(RoomEvent.ParticipantDisconnected, update)
        .on(RoomEvent.TrackSubscribed, update)
        .on(RoomEvent.TrackUnsubscribed, update)
        .on(RoomEvent.LocalTrackPublished, () => {
          update();
          void refreshCameras();
        })
        .on(RoomEvent.LocalTrackUnpublished, update)
        .on(RoomEvent.TrackMuted, update)
        .on(RoomEvent.TrackUnmuted, update)
        .on(RoomEvent.ActiveSpeakersChanged, update)
        .on(RoomEvent.Disconnected, () => {
          roomRef.current = null;
          setStatus('idle');
          setTiles([]);
        });

      await room.connect(credentials.url, credentials.token);
      setStatus('connected');
      refresh(room);
      void refreshCameras();
      // A blocked mic prompt must not abort an already-joined call.
      try {
        await room.localParticipant.setMicrophoneEnabled(true);
        setMic(true);
      } catch (cause) {
        setMic(false);
        useChat.getState().notify(`Microphone unavailable: ${(cause as Error).message}`, 'error');
      }
    } catch (cause) {
      await roomRef.current?.disconnect().catch(() => {});
      roomRef.current = null;
      setStatus('error');
      setError((cause as Error).message);
    }
  }, [conversationId, me.id, me.name, refresh, refreshCameras]);

  const toggleMic = useCallback(async () => {
    const room = roomRef.current;
    if (!room) return;
    const next = !room.localParticipant.isMicrophoneEnabled;
    try {
      await room.localParticipant.setMicrophoneEnabled(next);
      setMic(next);
    } catch (cause) {
      setMic(false);
      useChat.getState().notify(`Microphone unavailable: ${(cause as Error).message}`, 'error');
    }
  }, []);

  const toggleCamera = useCallback(async () => {
    const room = roomRef.current;
    if (!room) return;
    const next = !room.localParticipant.isCameraEnabled;
    try {
      await room.localParticipant.setCameraEnabled(next);
      setCamera(next);
      if (next) await refreshCameras();
    } catch (cause) {
      useChat.getState().notify(`Camera unavailable: ${(cause as Error).message}`, 'error');
    }
    refresh(room);
  }, [refresh, refreshCameras]);

  /**
   * Switches the active camera without touching audio or rejoining. LiveKit
   * replaces the local video track; a restart fallback covers SDK versions
   * where switchActiveDevice returns false.
   */
  const switchCamera = useCallback(async () => {
    const room = roomRef.current;
    if (!room || switchingCamera) return;
    const selection = selectCameras(cameras, room.getActiveDevice('videoinput'));
    if (!selection.canSwitch || !selection.nextId) {
      useChat.getState().notify('Only one camera is available on this device', 'info');
      return;
    }
    setSwitchingCamera(true);
    try {
      const switched = await room.switchActiveDevice('videoinput', selection.nextId);
      if (!switched) {
        const publication = room.localParticipant.getTrackPublication(Track.Source.Camera);
        const track = publication?.track as LocalTrack | undefined;
        if (!track || typeof track.restartTrack !== 'function') throw new Error('The active camera could not be replaced');
        await track.restartTrack({ deviceId: selection.nextId });
      }
      setActiveCameraId(selection.nextId);
      await refreshCameras();
      refresh(room);
    } catch (error) {
      useChat.getState().notify(`Camera switch failed: ${(error as Error).message}`, 'error');
    } finally {
      setSwitchingCamera(false);
    }
  }, [cameras, switchingCamera, refreshCameras, refresh]);

  const toggleScreen = useCallback(async () => {
    const room = roomRef.current;
    if (!room) return;
    const next = !room.localParticipant.isScreenShareEnabled;
    await room.localParticipant.setScreenShareEnabled(next).catch(() => {});
    setScreen(next);
    refresh(room);
  }, [refresh]);

  const toggleAgent = useCallback(async () => {
    if (!conversationId || agentBusy) return;
    setAgentBusy(true);
    try {
      await post('/api/livekit/agent', { conversationId, action: agent ? 'stop' : 'start' });
      setAgent(!agent);
    } catch (cause) {
      useChat.getState().notify((cause as Error).message, 'error');
    } finally {
      setAgentBusy(false);
    }
  }, [conversationId, agent, agentBusy]);

  /** Asks the in-call agent, streamed back over LiveKit Text Streams. */
  const ask = useCallback(
    async (prompt: string) => {
      const room = roomRef.current;
      if (!room || !conversationId) return false;
      const messageId = `live-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
      useChat.getState().addEphemeral(conversationId, messageId);
      const send = async () => {
        const writer = await room.localParticipant.streamText({
          topic: AI_REQUEST_TOPIC,
          attributes: { conversationId, messageId },
        });
        await writer.write(JSON.stringify({ prompt, conversationId, messageId, speak: true }));
        await writer.close();
      };
      try {
        const wait = waitForReplyStart(messageId, 2500);
        await send();
        if (await wait) return true;
        // The agent may still be finishing its join; one retry is safe because
        // the agent ignores duplicate messageIds.
        const retry = waitForReplyStart(messageId, 5000);
        await send();
        if (await retry) return true;
        useChat.getState().finishStream(conversationId, messageId);
        useChat.getState().notify('The AI agent did not answer. Check that it is in the call.', 'error');
        return false;
      } catch (cause) {
        useChat.getState().finishStream(conversationId, messageId);
        useChat.getState().notify((cause as Error).message, 'error');
        return false;
      }
    },
    [conversationId, waitForReplyStart],
  );

  useEffect(() => {
    return () => {
      const room = roomRef.current;
      roomRef.current = null;
      void room?.disconnect().catch(() => {});
    };
  }, [conversationId]);

  return {
    status,
    error,
    tiles,
    mic,
    camera,
    cameras,
    canSwitchCamera,
    activeCameraId,
    switchingCamera,
    switchCamera,
    screen,
    agent,
    agentBusy,
    join,
    leave,
    toggleMic,
    toggleCamera,
    toggleScreen,
    toggleAgent,
    ask,
    connected: status === 'connected',
  };
}

export type CallController = ReturnType<typeof useCall>;
